import type { RiskSummaryRow } from '../api/risk-summary';
import { parseDecimal } from './decimal';

export type RiskStatus = 'balanced' | 'warning' | 'risk' | 'ok';

// Risk thresholds mirror metycle-launchpad/src/components/hedging/RiskSummaryCards.tsx
const RISK_BALANCED_PCT = 0.02;
const RISK_WARNING_PCT = 0.25;

export const VARIANCE_THRESHOLD_MT = 0.01;

export const TICKER_LABEL: Record<string, string> = {
  CU: 'Copper',
  AL: 'Aluminum',
  ZN: 'Zinc',
  NI: 'Nickel',
  PB: 'Lead',
  SN: 'Tin',
  FX: 'EUR/USD',
};

export const EXCHANGE_LABEL: Record<string, string> = {
  CU: 'LME',
  AL: 'LME',
  ZN: 'LME',
  NI: 'LME',
  PB: 'LME',
  SN: 'LME',
  FX: 'FX',
};

// FX carries no payable-MT unit; treat it as base-currency notional.
export const UNIT_LABEL: Record<string, string> = {
  FX: 'EUR',
};

export const STATUS_CONFIG: Record<RiskStatus, { label: string; dot: string; text: string }> = {
  balanced: { label: 'Balanced', dot: 'bg-emerald-500', text: 'text-emerald-700' },
  warning: { label: 'Attention', dot: 'bg-amber-500', text: 'text-amber-700' },
  risk: { label: 'Needs Hedge', dot: 'bg-red-500', text: 'text-red-700' },
  ok: { label: 'No Exposure', dot: 'bg-slate-400', text: 'text-slate-500' },
};

export const NET_TONE_CLASS: Record<RiskStatus, string> = {
  balanced: 'text-emerald-600',
  warning: 'text-amber-600',
  risk: 'text-red-600',
  ok: 'text-muted-foreground',
};

export function classifyStatus(unhedged: number, hedgeable: number): RiskStatus {
  if (hedgeable <= 0) return 'ok';
  const absPct = Math.abs(unhedged) / hedgeable;
  if (absPct < RISK_BALANCED_PCT) return 'balanced';
  if (absPct < RISK_WARNING_PCT) return 'warning';
  return 'risk';
}

export function formatMt(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

export function formatNotional(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1000) {
    return `${(n / 1000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 })}K`;
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export function netSignChar(net: number): string {
  if (net === 0) return '';
  return net > 0 ? '+' : '−';
}

export interface DerivedCard {
  isFx: boolean;
  net: number;
  hedgeable: number;
  // Exposure value shown in the "Exposure" mini-stat. For metals this is the
  // payable-adjusted physical exposure (hedgeable_qty_mt summed across
  // orders). For FX it's the active-direction notional — long if any longs
  // exist, otherwise the short side. Mirrors RiskSummaryCards.tsx:158 in
  // metycle-launchpad.
  exposure: number;
  // Display value for the "Hedge" mini-stat. For metals the launchpad shows
  // `min(long, short) + |min(long, short)|`, i.e. 2 × matched leg, which is
  // gross matched volume across both sides. For FX it's just min(long, short)
  // — the matched side. We mirror that to stay visually identical to the
  // launchpad even though the metals formula doubles the matched leg.
  hedge: number;
  variance: number;
  hasVariance: boolean;
  status: RiskStatus;
  ticker: string;
  exchange: string;
  unit: string;
  format: (n: number) => string;
}

function activeFxLeg(long: number, short: number): number {
  return long > 0 ? long : short;
}

export function deriveCard(row: RiskSummaryRow): DerivedCard {
  const isFx = row.asset === 'FX';
  const long = parseDecimal(row.internalLongMt);
  const short = parseDecimal(row.internalShortMt);
  const net = parseDecimal(row.internalNetMt);
  const hedgeable = parseDecimal(row.hedgeableMt);
  const variance = parseDecimal(row.varianceMt);
  const matched = Math.min(long, short);
  const exposure = isFx ? activeFxLeg(long, short) : hedgeable;
  const hedge = isFx ? matched : matched + Math.abs(matched);
  return {
    isFx,
    net,
    hedgeable,
    exposure,
    hedge,
    variance,
    hasVariance: Math.abs(variance) > VARIANCE_THRESHOLD_MT,
    status: classifyStatus(net, exposure),
    ticker: TICKER_LABEL[row.asset] ?? row.asset,
    exchange: EXCHANGE_LABEL[row.asset] ?? '',
    unit: UNIT_LABEL[row.asset] ?? 'MT',
    format: isFx ? formatNotional : formatMt,
  };
}
